/**
 * Central, typed wrapper for localforage.
 * All durable data should be stored through this module, using wsc: prefixed keys.
 *
 * Design:
 * - All keys use the `wsc:` prefix (set automatically by buildKey).
 * - Logical keys should use format: `entity:version`, e.g., `trips:v1`, `settings:v2`.
 * - All operations are async and return Promise<T | null>.
 * - Cross-tab sync signals use lightweight localStorage toggles, not localforage.
 */

import localforage from 'localforage';

const STORAGE_PREFIX = 'wsc:';

/**
 * Build the full storage key by prepending the wsc: prefix.
 */
export function buildKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

/**
 * Retrieve an item from localforage with automatic type casting.
 * Returns null if the key does not exist or on error.
 */
export async function getItem<T>(key: string): Promise<T | null> {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const fullKey = buildKey(key);
    const value = await localforage.getItem<T>(fullKey);
    return value ?? null;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`getItem(${key}): failed to read from localforage`, e);
    }
    return null;
  }
}

/**
 * Store an item in localforage.
 * Throws on error (let the caller decide how to handle).
 */
export async function setItem<T>(key: string, value: T): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const fullKey = buildKey(key);
    await localforage.setItem(fullKey, value);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`setItem(${key}): failed to write to localforage`, e);
    }
    throw e;
  }
}

/**
 * Remove an item from localforage.
 * Silently succeeds if the key does not exist.
 */
export async function removeItem(key: string): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const fullKey = buildKey(key);
    await localforage.removeItem(fullKey);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`removeItem(${key}): failed to remove from localforage`, e);
    }
  }
}

/**
 * Clear all items with keys matching a given prefix (e.g., 'trips:', 'settings:').
 * The full prefix will be prepended with wsc:.
 */
export async function clearPrefix(prefix: string): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const fullPrefix = buildKey(prefix);
    const keysToRemove: string[] = [];

    await localforage.iterate<unknown, void>((_value, key) => {
      if (key && key.startsWith(fullPrefix)) {
        keysToRemove.push(key);
      }
    });

    for (const key of keysToRemove) {
      await localforage.removeItem(key);
    }
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(`clearPrefix(${prefix}): failed to clear prefix`, e);
    }
  }
}

/**
 * Broadcast a lightweight change signal via localStorage for cross-tab notifications.
 * This should only be called for signaling, not for storing actual data.
 * The signal will be cleaned up automatically after a brief delay.
 */
export function broadcastStorageChange(signalKey: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const stamp = Date.now().toString();
    window.localStorage.setItem(signalKey, stamp);
    window.setTimeout(() => {
      try {
        if (window.localStorage.getItem(signalKey) === stamp) {
          window.localStorage.removeItem(signalKey);
        }
      } catch (e) {
        // ignore cleanup errors
      }
    }, 500);
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`broadcastStorageChange(${signalKey}): failed`, e);
    }
  }
}

/**
 * Dispatch a custom event for same-tab subscribers.
 * Useful for notifying listeners without relying on localStorage events.
 */
export function dispatchStorageSignal(signalKey: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent(signalKey));
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`dispatchStorageSignal(${signalKey}): failed`, e);
    }
  }
}
